/**
 * Tolerance-cost ranking (PRD §4.6, sensitivity tier 3).
 *
 * Variance share answers "where does the spread come from". It does not answer
 * the question engineers actually have, which is "which tolerance do I pay to
 * tighten, and what do I get for it". Those differ whenever a large contributor
 * is already at the limit of what the process can hold, or when a small
 * contributor sits right against a spec limit so that a modest improvement
 * moves the yield a lot.
 *
 * The honest way to answer is to re-run: scale one variable's spread, redo the
 * analysis, and report the actual change in sigma and yield. That costs one
 * extra Monte Carlo run per variable, which is why the iteration count for
 * these probe runs is separate from the main run and defaults lower — the
 * ranking needs far less precision than the headline number does.
 *
 * An analytic RSS prediction is reported alongside for free. Where the two
 * disagree, the system is nonlinear and the measured column is the one to
 * trust.
 */

import type { CompiledModel, Project } from './model.ts';
import { buildModel } from './model.ts';
import { scaleSpread, variance as distVariance } from './distributions.ts';
import { runMonteCarlo } from './montecarlo.ts';
import { analyzeRss } from './rss.ts';
import { wilsonYield } from './stats.ts';

export interface ToleranceOption {
  varId: string;
  /** Spread multiplier applied to this variable, e.g. 0.5 for "halve it". */
  factor: number;

  /** Output sigma with the change applied. */
  stdDev: number;
  /** Fractional change in output sigma. Negative means an improvement. */
  stdDevDelta: number;

  /** Yield with the change applied; undefined when the output has no limits. */
  yieldPoint?: number;
  yieldDelta?: number;
  ppm?: number;
  ppmDelta?: number;

  /** Sigma predicted by the linearised RSS model, for comparison. */
  predictedStdDev: number;
  /**
   * True when the measured and predicted sigma agree within 5%. When false the
   * linear extrapolation is not describing this system.
   */
  predictionHolds: boolean;
}

export interface ToleranceRanking {
  outputId: string;
  baselineStdDev: number;
  baselineYield?: number;
  baselinePpm?: number;
  iterations: number;
  /** Tightening options, best improvement first. */
  tighten: ToleranceOption[];
  /** Loosening options, least damage first — where cost can be recovered. */
  loosen: ToleranceOption[];
  notes: string[];
}

export interface ToleranceOptions {
  /** Iterations per probe run. Lower than the headline run on purpose. */
  iterations?: number;
  seed?: number;
  /** Spread multiplier for the "tighten" column. */
  tightenFactor?: number;
  /** Spread multiplier for the "loosen" column. */
  loosenFactor?: number;
  /** Skip variables contributing less than this share of variance. */
  minContribution?: number;
}

/** Produce a project with one variable's spread scaled. */
export function withScaledVariable(
  project: Project,
  varId: string,
  factor: number,
): Project {
  return {
    ...project,
    variables: project.variables.map((v) =>
      v.id === varId ? { ...v, distribution: scaleSpread(v.distribution, factor) } : v,
    ),
  };
}

/**
 * Analytic prediction of the output sigma if one variable's spread is scaled.
 *
 * Under a first-order model the variable contributes (df/dx * sigma)^2 to the
 * output variance, so scaling its spread by f scales that term by f^2 and
 * leaves everything else alone. Exact for a linear system, indicative
 * otherwise.
 */
function predictStdDev(
  model: CompiledModel,
  outputIndex: number,
  varId: string,
  factor: number,
): number {
  const rss = analyzeRss(model, outputIndex);
  const contribution = rss.contributions.find((c) => c.varId === varId);
  if (!contribution) return rss.stdDev;

  const total = rss.stdDev * rss.stdDev;
  const term = contribution.variance;
  const scaled = total - term + term * factor * factor;
  return Math.sqrt(Math.max(0, scaled));
}

export function rankTolerances(
  project: Project,
  outputIndex: number,
  options: ToleranceOptions = {},
): ToleranceRanking {
  const iterations = options.iterations ?? 50_000;
  const seed = options.seed ?? project.seed;
  const tightenFactor = options.tightenFactor ?? 0.5;
  const loosenFactor = options.loosenFactor ?? 2;
  const minContribution = options.minContribution ?? 0.001;

  const baseModel = buildModel(project);
  const outputId = baseModel.outputs[outputIndex].spec.id;
  const spec = baseModel.outputs[outputIndex].spec;
  const hasLimits = spec.lsl !== undefined || spec.usl !== undefined;

  // Every probe shares the baseline's seed and iteration count, so differences
  // between them are the tolerance change and nothing else. Using a different
  // stream per probe would bury a small real effect under sampling noise.
  const baseline = runMonteCarlo(baseModel, { iterations, seed });
  const baseStats = baseline.outputs[outputIndex].stats;
  const baseYield = hasLimits
    ? wilsonYield(baseStats.inSpec, baseStats.count).point
    : undefined;
  const basePpm = baseYield !== undefined ? (1 - baseYield) * 1e6 : undefined;

  const notes: string[] = [];

  // Only variables that actually carry variance are worth probing.
  const candidates = project.variables.filter((v) => {
    if (distVariance(v.distribution) <= 0) return false;
    const s = baseline.outputs[outputIndex].sensitivities.find((x) => x.varId === v.id);
    return !s || Math.abs(s.contribution) >= minContribution || Math.abs(s.spearman) > 0.02;
  });

  if (candidates.length === 0) {
    notes.push('No variable carries enough variance for tightening to change this output.');
  }

  const probe = (varId: string, factor: number): ToleranceOption => {
    const probed = buildModel(withScaledVariable(project, varId, factor));
    const run = runMonteCarlo(probed, { iterations, seed });
    const stats = run.outputs[outputIndex].stats;

    const y = hasLimits ? wilsonYield(stats.inSpec, stats.count).point : undefined;
    const predicted = predictStdDev(baseModel, outputIndex, varId, factor);
    const measured = stats.stdDev;

    return {
      varId,
      factor,
      stdDev: measured,
      stdDevDelta: baseStats.stdDev > 0 ? measured / baseStats.stdDev - 1 : 0,
      yieldPoint: y,
      yieldDelta: y !== undefined && baseYield !== undefined ? y - baseYield : undefined,
      ppm: y !== undefined ? (1 - y) * 1e6 : undefined,
      ppmDelta:
        y !== undefined && basePpm !== undefined ? (1 - y) * 1e6 - basePpm : undefined,
      predictedStdDev: predicted,
      predictionHolds:
        measured > 0 && Math.abs(predicted / measured - 1) <= 0.05,
    };
  };

  const tighten = candidates
    .map((v) => probe(v.id, tightenFactor))
    // Most negative sigma delta first: the biggest improvement per unit of pain.
    .sort((a, b) => a.stdDevDelta - b.stdDevDelta);

  const loosen = candidates
    .map((v) => probe(v.id, loosenFactor))
    // Least damage first: where a looser tolerance buys cost back most cheaply.
    .sort((a, b) => a.stdDevDelta - b.stdDevDelta);

  if (tighten.some((t) => !t.predictionHolds)) {
    notes.push(
      'For some variables the measured effect differs from the linear prediction by more ' +
        'than 5%, so this system does not respond proportionally to tolerance changes. ' +
        'Use the measured columns.',
    );
  }

  const best = tighten[0];
  if (best && best.stdDevDelta > -0.02) {
    notes.push(
      `Halving any single tolerance changes the output sigma by at most ` +
        `${(Math.abs(best.stdDevDelta) * 100).toFixed(1)}%. The spread is spread thin across ` +
        'many contributors, so no single part is worth tightening on its own.',
    );
  }

  return {
    outputId,
    baselineStdDev: baseStats.stdDev,
    baselineYield: baseYield,
    baselinePpm: basePpm,
    iterations,
    tighten,
    loosen,
    notes,
  };
}
